/**
 * 消息中心（T6）—— 站内通知列表。
 * 数据源：GET /notification/my（当前登录用户，按时间倒序）。
 * 支持：未读高亮、点击标记已读、全部已读、分类筛选（全部/待办/系统）。
 */
import { Box, Typography, CircularProgress, Snackbar, Alert, ButtonBase } from '@mui/material';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getMyNotifications,
  getUnreadCount,
  markNotificationRead,
  markAllNotificationsRead,
  type NotificationItem,
} from '@/api/notification';
import { MobileAppBar, IconBtn, SegTabs, EmptyState, StatusPill, cardSx } from '@/components/mobile/ui';
import { T } from '@/theme/tokens';

type TabValue = 'ALL' | 'NODE_TODO' | 'SYSTEM';

export default function Messages() {
  const navigate = useNavigate();
  const [tab, setTab] = useState<TabValue>('ALL');
  const [items, setItems] = useState<NotificationItem[]>([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(true);
  const [snack, setSnack] = useState<{ msg: string; sev: 'success' | 'error' | 'info' } | null>(null);

  const load = useCallback(async (t: TabValue = tab) => {
    setLoading(true);
    try {
      const category = t === 'ALL' ? undefined : t;
      const [list, count] = await Promise.all([
        getMyNotifications({ limit: 50, category }).catch(() => []),
        getUnreadCount().catch(() => 0),
      ]);
      setItems(Array.isArray(list) ? list : []);
      setUnread(typeof count === 'number' ? count : 0);
    } catch {
      /* 加载失败静默，展示空态 */
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => {
    load();
  }, [load]);

  const handleTabChange = (v: TabValue) => {
    setTab(v);
    load(v);
  };

  const handleRead = async (item: NotificationItem) => {
    // 引用了订单的通知（如新的履约待办 / 回款提醒）→ 标记已读后直达作业页
    if (item.refType === 'ORDER' && item.refId) {
      if (!item.read) {
        try {
          await markNotificationRead(item.id);
          setItems((prev) => prev.map((x) => (x.id === item.id ? { ...x, read: true } : x)));
          setUnread((u) => Math.max(0, u - 1));
        } catch {
          /* 跳转不依赖标记结果 */
        }
      }
      navigate(`/mobile/task/${item.refId}`, { state: { orderNo: item.refId } });
      return;
    }
    // 其余通知仅标记已读
    if (item.read) return;
    try {
      await markNotificationRead(item.id);
      setItems((prev) => prev.map((x) => (x.id === item.id ? { ...x, read: true } : x)));
      setUnread((u) => Math.max(0, u - 1));
    } catch (e: unknown) {
      setSnack({ msg: (e as { message?: string })?.message || '操作失败', sev: 'error' });
    }
  };

  const handleReadAll = async () => {
    try {
      const n = await markAllNotificationsRead(tab === 'ALL' ? undefined : tab);
      setItems((prev) => prev.map((x) => ({ ...x, read: true })));
      setUnread(0);
      setSnack({ msg: `已全部标记已读（${n ?? 0} 条）`, sev: 'success' });
    } catch (e: unknown) {
      setSnack({ msg: (e as { message?: string })?.message || '操作失败', sev: 'error' });
    }
  };

  const fmtTime = (s?: string): string => {
    if (!s) return '';
    const d = new Date(s);
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  const categoryMeta = (c?: string) =>
    c === 'NODE_TODO'
      ? { label: '待办', tone: 'amber' as const, icon: <AssignmentOutlinedIcon /> }
      : { label: '系统', tone: 'blue' as const, icon: <NotificationsNoneOutlinedIcon /> };

  return (
    <Box sx={{ pb: 2 }}>
      <MobileAppBar
        title="消息"
        right={
          unread > 0 ? (
            <IconBtn label="全部已读" onClick={handleReadAll} badge={unread}>
              <DoneAllIcon />
            </IconBtn>
          ) : undefined
        }
      />

      <Box sx={{ px: 2, pt: 2 }}>
        {/* 分类 */}
        <SegTabs<TabValue>
          value={tab}
          onChange={handleTabChange}
          options={[
            { value: 'ALL', label: '全部', count: unread > 0 ? unread : undefined },
            { value: 'NODE_TODO', label: '待办提醒' },
            { value: 'SYSTEM', label: '系统' },
          ]}
          sx={{ mb: '14px' }}
        />

        {/* 列表 */}
        {loading && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CircularProgress size={26} />
          </Box>
        )}
        {!loading && items.length === 0 && (
          <EmptyState
            icon={<NotificationsNoneOutlinedIcon />}
            title="暂无消息"
            desc="节点提醒、异常通知与协同消息会显示在这里"
          />
        )}
        {!loading &&
          items.map((item) => {
            const cm = categoryMeta(item.category);
            const jumpable = item.refType === 'ORDER' && Boolean(item.refId);
            return (
              <ButtonBase
                key={item.id}
                onClick={() => handleRead(item)}
                sx={{
                  ...cardSx,
                  width: '100%',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1.5,
                  p: '14px',
                  mb: 1.5,
                  textAlign: 'left',
                  fontFamily: 'inherit',
                  position: 'relative',
                  ...(item.read ? {} : { borderColor: T.brandLine }),
                }}
              >
                <Box
                  sx={{
                    position: 'relative',
                    width: 38,
                    height: 38,
                    borderRadius: '11px',
                    flexShrink: 0,
                    bgcolor: item.read ? T.line2 : T.brandSoft,
                    color: item.read ? T.ink3 : T.brand,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    '& .MuiSvgIcon-root': { fontSize: 20 },
                  }}
                >
                  {cm.icon}
                  {!item.read && (
                    <Box
                      component="span"
                      sx={{ position: 'absolute', top: -2, right: -2, width: 9, height: 9, borderRadius: '50%', bgcolor: T.danger, border: '2px solid #fff' }}
                    />
                  )}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography
                      noWrap
                      sx={{ flex: 1, minWidth: 0, fontSize: '14px', fontWeight: item.read ? 600 : 700, color: item.read ? T.ink2 : T.ink }}
                    >
                      {item.title || '通知'}
                    </Typography>
                    <StatusPill tone={item.read ? 'grey' : cm.tone}>{cm.label}</StatusPill>
                  </Box>
                  {item.content && (
                    <Typography
                      sx={{
                        fontSize: '12.5px',
                        color: T.ink2,
                        mt: '5px',
                        lineHeight: 1.55,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}
                    >
                      {item.content}
                    </Typography>
                  )}
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                    <Typography sx={{ fontSize: '11.5px', color: T.ink3, fontFamily: T.mono }}>
                      {fmtTime(item.createdAt)}
                    </Typography>
                    {jumpable && (
                      <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', fontSize: '12px', fontWeight: 600, color: T.brand }}>
                        去处理
                        <ChevronRightIcon sx={{ fontSize: 16 }} />
                      </Box>
                    )}
                  </Box>
                </Box>
              </ButtonBase>
            );
          })}
      </Box>

      <Snackbar
        open={!!snack}
        autoHideDuration={3000}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        onClose={() => setSnack(null)}
        sx={{ bottom: { xs: 80 } }}
      >
        <Alert severity={snack?.sev} sx={{ fontSize: '13px' }}>
          {snack?.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
